import React, { Suspense, useRef } from 'react';
import { logger } from '@fiverr-private/obs';
import { useIntersectionObserver } from '@fiverr-private/hooks';
import { Container } from '@fiverr-private/layout_components';
import { metric } from '../prefixedStats';

const reportLoadingError = (componentName, error) => {
    metric.count(`lazy_loading.fetch_error.${componentName}`);
    logger.warn(error, {
        description: 'Failed to lazy load asset',
        componentName,
    });
};

class LazyImportErrorBoundary extends React.Component {
    static getDerivedStateFromError() {
        return { hasError: true };
    }

    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    render() {
        if (this.state.hasError) {
            return null;
        }

        return this.props.children;
    }
}

export const lazyImport = (componentName, loader) => {
    const LazyLoaded = React.lazy(() =>
        loader().catch((error) => {
            reportLoadingError(componentName, error);
            throw error;
        })
    );

    // eslint-disable-next-line react/display-name
    return (props) => {
        const observableRef = useRef(null);

        const entry = useIntersectionObserver(observableRef, {
            rootMargin: '200px',
            threshold: 0,
            once: true,
        });

        return (
            <Container ref={observableRef}>
                {entry?.isIntersecting && (
                    <LazyImportErrorBoundary>
                        <Suspense fallback={null}>
                            <LazyLoaded {...props} />
                        </Suspense>
                    </LazyImportErrorBoundary>
                )}
            </Container>
        );
    };
};
